import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { PrecisionManufacturing } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import type { StockCoverage } from '../../types';

const LEVEL_COLOR: Record<string, 'error' | 'warning' | 'success'> = { critical: 'error', warning: 'warning', ok: 'success' };

/** Stock de producto terminado con los días que alcanza al ritmo de venta actual. */
const MobileStockPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StockCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setItems(await api.getStockCoverage({ includeAll: true }));
      } catch (e: any) {
        setError(e?.response?.data?.message || 'No se pudo cargar el stock');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => !q || i.productName.toLowerCase().includes(q) || (i.sku || '').toLowerCase().includes(q));
  }, [items, search]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Stack spacing={1.5}>
      <TextField placeholder="Buscar producto…" value={search} onChange={e => setSearch(e.target.value)} InputProps={{ sx: { fontSize: 18, minHeight: 56 } }} />
      {error && <Alert severity="error">{error}</Alert>}
      {!error && items.length === 0 && (
        <Alert severity="info">Todavía no hay productos con control de stock.</Alert>
      )}
      {filtered.map(i => (
        <Card key={i.productId} variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box>
                <Typography variant="h6" fontWeight={700}>{i.productName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {i.dailySales > 0 ? `Se venden ~${i.dailySales} por día` : 'Sin ventas en los últimos 30 días'}
                  {i.minStock > 0 ? ` · mínimo ${i.minStock}` : ''}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="h4" fontWeight={800}>{i.currentStock}</Typography>
                <Typography variant="caption" color="text.secondary">{i.unitOfMeasure}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }} spacing={1}>
              <Chip
                color={LEVEL_COLOR[i.level]}
                label={i.daysOfCoverage != null ? `Alcanza ~${i.daysOfCoverage} días` : i.belowMinStock ? 'Bajo el mínimo' : 'OK'}
                sx={{ fontWeight: 700 }}
              />
              {i.level !== 'ok' && i.hasRecipe && (
                <Button variant="contained" color="warning" startIcon={<PrecisionManufacturing />} sx={{ minHeight: 44 }}
                  onClick={() => navigate(`/m/producir?productId=${i.productId}&qty=${i.suggestedProduction}`)}>
                  Producir
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default MobileStockPage;
